import type { Submission } from '@/api/v2Client';

/**
 * phase-dashboard-coherence item 6 (D-050) — THE one source for a dashboard
 * parent row's "face": the sequence whose snapshot the parent displays AND
 * opens on row-click. Display and navigation both read this, so they cannot
 * disagree.
 *
 * Rules (Sharon, 2026-08-01):
 *  - The face is the HIGHEST-numbered sequence currently VISIBLE under the
 *    active filter.
 *  - Hidden (archived) sequences never drive the parent: with "Show hidden"
 *    off, the face falls to the highest shown sequence.
 *  - When hidden sequences are SHOWN they count — a withdrawn (hidden)
 *    sequence may legitimately be the face, because sequence numbers are
 *    consumed (next is always highest+1).
 *  - The count annotation stays truthful to the record regardless of filter:
 *    "3 sequences (1 hidden)".
 *  - All children hidden → no face; the parent renders the explicit
 *    "all sequences hidden" state and its row-click expands only.
 */
export interface ParentFace {
  /** The sequence the parent row displays and opens; null when none visible. */
  readonly face: Submission | null;
  /** The children currently visible under the active filter, sorted by sequence. */
  readonly visible: readonly Submission[];
  readonly totalCount: number;
  readonly hiddenCount: number;
  readonly allHidden: boolean;
  /**
   * phase-dashboard-honest-counts (Sharon's D-050 amendment, 2026-08-02) —
   * the SEQ-cell note renders ONLY when sequences are hidden (when nothing
   * is hidden, expanding the row already answers "how many"). Wording is
   * state-dependent so it reads correctly in both toggle states:
   *   Show hidden OFF → "1 hidden"            (it is not in the list)
   *   Show hidden ON  → "including 1 hidden"  (it is in the list, badged)
   * Null when nothing is hidden — no annotation renders at all.
   */
  readonly hiddenNote: string | null;
  /**
   * phase-dashboard-honest-counts — the parent DOCS number: the sum of the
   * VISIBLE children's per-sequence document counts, so the parent total is
   * always reconstructible from the child rows on screen and stays in
   * lockstep with the visible tree in both toggle states. Null when no
   * visible child carries a count (old API rows) or nothing is visible.
   */
  readonly visibleDocs: number | null;
}

function isHiddenSequence(s: Submission): boolean {
  return s.IsArchived === true;
}

export function computeParentFace(
  sequences: readonly Submission[],
  showHidden: boolean,
): ParentFace {
  const sorted = [...sequences].sort((a, b) =>
    (a.SequenceNumber ?? '').localeCompare(b.SequenceNumber ?? ''),
  );
  const hiddenCount = sorted.filter(isHiddenSequence).length;
  const visible = showHidden ? sorted : sorted.filter((s) => !isHiddenSequence(s));
  const face = visible.length > 0 ? visible[visible.length - 1]! : null;
  const totalCount = sorted.length;
  const allHidden = totalCount > 0 && hiddenCount === totalCount;

  const hiddenNote =
    hiddenCount === 0
      ? null
      : showHidden
        ? `including ${hiddenCount} hidden`
        : `${hiddenCount} hidden`;

  const anyDocs = visible.some((s) => s.TotalDocs != null);
  const visibleDocs =
    visible.length > 0 && anyDocs
      ? visible.reduce((sum, s) => sum + (Number(s.TotalDocs) || 0), 0)
      : null;

  return {
    face,
    visible,
    totalCount,
    hiddenCount,
    allHidden,
    hiddenNote,
    visibleDocs,
  };
}
